import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ReactGA from "react-ga4";
import { Helmet } from 'react-helmet';
import BackButton from '../Components/backbutton/BackButton';
import ShareClub from "../Components/ShareClub";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import GroupIcon from "@mui/icons-material/Group";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import "./EventDetail.css";
import { sanitizeHtml } from '../utils/sanitizeHtml';

// Import the diplo imags
import diplo_sf from '../assets/eventImages/diplo_sf.jpg';
import diplo_san_diego from '../assets/eventImages/diplo_san_diego.jpg';

// Sample event data - in production, this would come from an API or data file
const SAMPLE_EVENTS = {
  "san-diego": {
    "diplos-run-club-san-diego": {
      active: false,
      id: "diplos-run-club-san-diego",
      title: "Diplo's Run Club San Diego",
      clubName: "Diplo's Run Club",
      city: "San Diego",
      image: diplo_san_diego,
      date: "Saturday, November 8th, 2025",
      time: "7:00 AM PST",
      location: "Gallagher Square, Petco Park",
      address: "100 Park Blvd, San Diego, CA 92101",
      distance: "5K",
      difficulty: "All Levels Welcome",
      description: "For the first time ever, Diplo's Run Club is hitting the streets of San Diego, California! Home to several EDM festivals, America's Finest City is known for its vibrant electronic music scene with past performances from Diplo himself at Nova San Diego and the Mad Decent Block Party. This fall, you can catch another epic performance at the main stage in Gallagher Square at Petco Park after Diplo's Run Club 5K. Starting and finishing at Gallagher Square, the 5K run will take you through the historic Gaslamp Quarter, showing off the best of downtown San Diego. After you cross the finish line, party the day away with a post-race concert featuring Diplo & Friends!",
      runDetails: [
        "5K route through the Gaslamp Quarter",
        "Start and finish at Gallagher Square",
        "Live post-race concert from Diplo & Friends",
        "Festival meets fitness experience",
        "All levels welcome"
      ],
      howToRegister: [
        "Register at diplosrunclub.com/san-diego",
        "Follow @diplosrunclub on Instagram for updates",
        "Email info@diplosrunclub.com with any questions"
      ],
      meetingPoint: {
        description: "Meet at the entrance to Gallagher Square at Petco Park. Look for Diplo's Run Club signage and volunteers.",
        parkingInfo: "Public parking available around Petco Park and Gaslamp Quarter. Early arrival recommended.",
        publicTransport: "Easily accessible via San Diego Trolley. Exit at Gaslamp Quarter Station."
      },
      tags: ["festival", "live music", "downtown", "diplo", "5K"],
      contactInfo: {
        instagram: "@diplosrunclub",
        email: "info@diplosrunclub.com"
      }
    }
  },
  "san-francisco": {
    "diplos-run-club-san-francisco": {
      active: false,
      id: "diplos-run-club-san-francisco",
      title: "Diplo's Run Club San Francisco",
      clubName: "Diplo's Run Club",
      city: "San Francisco",
      image: diplo_sf,
      date: "October 11th, 2025",
      time: "7:00 AM PST",
      location: "Robin Williams Meadow at Golden Gate Park ",
      address: "Robin Williams Meadow at Golden Gate Park ",
      distance: "5K",
      difficulty: "All Levels Welcome",
      description: "Diplo's Run Club is returning to the Bay Area for round two! After a successful first year with more than 15,000 of you running & raving the day away, we're back — bigger & better than ever.",
      runDetails: [
        "5K route past the Bay Bridge, Embarcadero, and Oracle Park",
        "Start and finish at Robin Williams Meadow",
        "Stunning bay and skyline views",
        "Post-race concert with Diplo & Friends",
        "Festival meets fitness experience"
      ],
      howToRegister: [
        "Register at diplosrunclub.com/san-francisco",
        "Follow @diplosrunclub on Instagram for updates",
        "Email info@diplosrunclub.com with any questions"
      ],
      meetingPoint: {
        description: "Meet at Robin Williams Meadow at Golden Gate Park",
        parkingInfo: "Paid parking nearby; public transport recommended.",
      },
      tags: ["festival", "live music", "waterfront", "diplo", "5K"],
      contactInfo: {
        instagram: "@diplosrunclub",
        email: "info@diplosrunclub.com"
      }
    }
  }
};


function EventDetail({ city }) {
  const { city: cityParam, eventId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [event, setEvent] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Get event data based on city and eventId
    // Use city from URL params directly (already in slug format)
    const cityEvents = SAMPLE_EVENTS[cityParam];
    const eventData = cityEvents?.[eventId];
    
    if (eventData) {
      setEvent(eventData);
      const gaEventData = {
        category: "User",
        action: "View Event",
        label: eventData.title,
      };
      ReactGA.event(gaEventData);
      console.log("Data sent to Google Analytics:", gaEventData);
    }
    
    setIsLoading(false);
  }, [cityParam, eventId]);

  useEffect(() => {
    // Load Instagram embed script
    const script = document.createElement('script');
    script.src = '//www.instagram.com/embed.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    if (window.instgrm) {
      window.instgrm.Embeds.process();
    }
  }, [event]);

  if (isLoading) {
    return <div className="event-loading">Loading event details...</div>;
  }

  if (!event) {
    return (
      <div className="event-not-found">
        <h2>Event not found</h2>
        <p>The event you're looking for doesn't exist or may have been removed.</p>
      </div>
    );
  }

  return (
    <div className="event-detail">
      <Helmet>
        <title>{event.title} - {event.city} | Clubsta Events</title>
        <meta name="description" content={event.description} />
        <meta name="keywords" content={`${event.title}, ${event.city}, running event, ${event.tags?.join(', ')}`} />
      </Helmet>

      <div className="event-header">
        <BackButton />
        <ShareClub
          clubUrl={`https://www.clubsta.co/events/${cityParam}/${eventId}`}
          clubTitle={event.title}
        />
      </div>

      <div className="event-container">
        {/* Event Title */}
        <div className="event-title-section">
          <h1 className="event-title">{event.title}</h1>
          <p className="event-club-name">{event.clubName}, {event.city}</p>
        </div>

        {/* Main Content Area */}
        <div className="event-content">
          <div className="event-left-column">
            {/* Event Image */}
            <div className="event-image-container">
              <img src={event.image} alt={event.title} className="event-image" />
            </div>
          </div>

          <div className="event-right-column">
            {/* Event Description */}
            <section className="event-section">
              <h2>Event Description</h2>
              <p className="event-description">{event.description}</p>
              {event.extendedDescription && (
                <p className="event-extended-description">{event.extendedDescription}</p>
              )}
            </section>

            {/* Run Details */}
            <section className="event-section">
              <h2>Run Details</h2>
              <div className="event-details-grid">
                <div className="detail-item">
                  <CalendarTodayIcon className="detail-icon" />
                  <span className="detail-text">{event.date}</span>
                </div>
                <div className="detail-item">
                  <AccessTimeIcon className="detail-icon" />
                  <span className="detail-text">{event.time}</span>
                </div>
                <div className="detail-item">
                  <DirectionsRunIcon className="detail-icon" />
                  <span className="detail-text">{event.distance}</span>
                </div>
                <div className="detail-item">
                  <LocationOnIcon className="detail-icon" />
                  <span className="detail-text">{event.location}</span>
                </div>
              </div>
            </section>

            {/* What to Expect */}
            {event.runDetails && (
              <section className="event-section">
                <h2>What to Expect</h2>
                <div className="run-highlights">
                  <ul>
                    {event.runDetails.map((detail, index) => (
                      <li key={index}>{detail}</li>
                    ))}
                  </ul>
                </div>
              </section>
            )}

            {/* How to Register */}
            <section className="event-section">
              <h2>How to Register</h2>
              <div className="registration-info">
                {event.howToRegister && (
                  <ul>
                    {event.howToRegister.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="contact-buttons">
                {event.contactInfo?.instagram && (
                  <a 
                    href={`https://instagram.com/${event.contactInfo.instagram.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-button primary"
                  >
                    Register on Instagram
                  </a>
                )}
                {event.contactInfo?.email && (
                  <a 
                    href={`mailto:${event.contactInfo.email}`}
                    className="contact-button secondary"
                  >
                    Email for Info
                  </a>
                )}
              </div>
            </section>

            {/* Meeting Point */}
            <section className="event-section">
              <h2>Meeting Point</h2>
              <div className="meeting-point-info">
                <div className="meeting-location">
                  <LocationOnIcon className="location-icon" />
                  <div>
                    <strong>{event.location}</strong>
                    <p>{event.address}</p>
                  </div>
                </div>
                <p className="meeting-description">{event.meetingPoint?.description}</p>
              </div>
            </section>

            {/* Instagram Feed */}
            {event.instagramEmbed && (
              <section className="event-section">
                <h2>Instagram Feed</h2>
                <div className="instagram-feed-container">
                  <div 
                    className="instagram-embed"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(event.instagramEmbed) }}
                  />
                  <p className="instagram-follow">
                    Follow <a href={`https://instagram.com/${event.contactInfo?.instagram?.replace('@', '')}`} target="_blank" rel="noopener noreferrer">
                      {event.contactInfo?.instagram}
                    </a> for more updates!
                  </p>
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventDetail; 